import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, Mail } from 'lucide-react';
import { LogoMark } from '@/components/Logo';
import {
  ERROR_CODES,
  type AuthenticatedResponse,
  type LoginChallengeResponse,
  type LoginResponse,
} from '@nlinks/shared';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { Alert, Button, Field, Input } from '@/components/ui';

type Stage = 'credentials' | 'otp';

export function LoginPage() {
  const { user, signIn, initializing } = useAuth();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>('credentials');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<LoginChallengeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const codeInputRef = useRef<HTMLInputElement>(null);

  // Countdown to expiry, so the user knows whether to wait or request a new code.
  useEffect(() => {
    if (!challenge) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(challenge.expiresAt).getTime() - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [challenge]);

  useEffect(() => {
    if (stage === 'otp') codeInputRef.current?.focus();
  }, [stage]);

  if (initializing) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="skeleton h-12 w-12 rounded-full" />
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;

  const submitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const response = await api.post<LoginResponse>(
        '/api/auth/login',
        { identifier, password },
        { skipAuth: true },
      );

      // The server decides whether a second factor is required
      // (REQUIRE_EMAIL_OTP). When it is off, login is already complete and the
      // response carries the session — showing a code screen would strand the
      // user in front of a field nothing will ever fill.
      if (!response.requiresOtp) {
        signIn(response);
        navigate('/', { replace: true });
        return;
      }

      setChallenge(response);
      setStage('otp');
      // Dev convenience: with MAIL_DEV_ECHO on, the server hands back the code
      // so a local setup needs no SMTP relay at all.
      if (response.devCode) {
        setCode(response.devCode);
        setNotice(`Development mode — code prefilled: ${response.devCode}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setBusy(true);
    try {
      const response = await api.post<AuthenticatedResponse>(
        '/api/auth/verify-otp',
        { challengeId: challenge.challengeId, code },
        { skipAuth: true },
      );
      signIn(response);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        // A dead challenge can't be retried — send them back to step one.
        if (err.code === ERROR_CODES.OTP_EXPIRED || err.code === ERROR_CODES.OTP_TOO_MANY_ATTEMPTS) {
          setStage('credentials');
          setChallenge(null);
          setCode('');
        }
      } else {
        setError('Could not verify that code. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!challenge) return;
    setError(null);
    setBusy(true);
    try {
      const response = await api.post<LoginChallengeResponse>(
        '/api/auth/resend-otp',
        { challengeId: challenge.challengeId },
        { skipAuth: true },
      );
      setChallenge(response);
      setNotice(response.devCode ? `Development mode — new code: ${response.devCode}` : 'A new code is on its way.');
      if (response.devCode) setCode(response.devCode);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not resend the code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <LogoMark className="mb-4 h-14 w-14" />
          <h1 className="text-2xl font-semibold tracking-tight text-slate-100">
            N<span className="font-normal text-slate-400">Links</span>
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">Email link verification &amp; mailbox monitoring</p>
        </div>

        <div className="card animate-slide-up p-6 sm:p-8">
          {stage === 'credentials' ? (
            <form onSubmit={submitCredentials} className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-100">Sign in</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Use the email address and password your administrator gave you.
                </p>
              </div>

              {error ? <Alert severity="error">{error}</Alert> : null}

              <Field label="Email address" htmlFor="identifier" required>
                <Input
                  id="identifier"
                  type="email"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="username"
                  autoFocus
                  required
                />
              </Field>

              <Field label="Password" htmlFor="password" required>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  required
                />
              </Field>

              <Button type="submit" loading={busy} className="w-full" size="lg" icon={<KeyRound className="h-4 w-4" />}>
                Continue
              </Button>

            </form>
          ) : (
            <form onSubmit={submitOtp} className="space-y-5">
              <button
                type="button"
                onClick={() => {
                  setStage('credentials');
                  setChallenge(null);
                  setCode('');
                  setError(null);
                  setNotice(null);
                }}
                className="-ml-1 flex items-center gap-1.5 text-sm text-slate-500 transition hover:text-slate-300"
              >
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                Back
              </button>

              <div>
                <h2 className="text-lg font-semibold text-slate-100">Check your email</h2>
                <p className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-slate-500">
                  <Mail className="h-3.5 w-3.5" aria-hidden />
                  Code sent to <span className="font-medium text-slate-300">{challenge?.maskedEmail}</span>
                </p>
              </div>

              {error ? <Alert severity="error">{error}</Alert> : null}
              {notice ? <Alert severity="info">{notice}</Alert> : null}

              <Field
                label="Verification code"
                htmlFor="code"
                required
                hint={secondsLeft > 0 ? `Expires in ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}` : 'This code has expired — request a new one.'}
              >
                <Input
                  ref={codeInputRef}
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="000000"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  className="text-center font-mono text-2xl tracking-[0.5em]"
                />
              </Field>

              <Button type="submit" loading={busy} className="w-full" size="lg" disabled={code.length < 4}>
                Verify and sign in
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={resend}
                  disabled={busy}
                  className="text-sm text-brand-400 transition hover:text-brand-300 disabled:opacity-50"
                >
                  Didn&apos;t get it? Send a new code
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-600">
          Accounts are created by an administrator. Contact yours if you don&apos;t have credentials.
        </p>
      </div>
    </div>
  );
}
